import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useState, Suspense, lazy, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { css } from '@emotion/react';
import AdminLogin from './AdminLogin';
import { useSiteData } from '@/context/SiteDataContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import SplitLetters from './SplitLetters';
import { publicAssetUrl } from '@/utils/publicAssetUrl';
const HomePage = lazy(() => import('@/pages/HomePage'));
const RolunkPage = lazy(() => import('@/pages/RolunkPage'));
const KapcsolatPage = lazy(() => import('@/pages/KapcsolatPage'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const routeFallback = (_jsx("div", { css: css `
      min-height: 50vh;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      font-size: 0.95rem;
    `, children: "Bet\u00F6lt\u00E9s\u2026" }));
/** Fő navigáció – csak a négy fő menüpont (stílus: minimalcar-mock.css .navigation) */
const MainNavLinks = ({ onNavigate }) => (_jsxs(_Fragment, { children: [_jsx("a", { href: "/#bemutatkozas", onClick: onNavigate, children: "Bemutatkoz\u00E1s" }), _jsx("a", { href: "/#keszpenzes-autofelvasarlas", onClick: onNavigate, children: "Aut\u00F3felv\u00E1s\u00E1rl\u00E1s" }), _jsx("a", { href: "/#hasznaltauto-eladas", onClick: onNavigate, children: "Haszn\u00E1ltaut\u00F3k" }), _jsx("a", { href: "/#kapcsolat", onClick: onNavigate, children: "Kapcsolat" })] }));
/** Bejelentkezés nélkül: login; belépve: AdminPanel + Outlet (gyerek Route-ok a Layout-ban). */
const AdminEntryWrapper = ({ isLoggedIn, onLogin, onLogout }) => {
    const navigate = useNavigate();
    if (!isLoggedIn) {
        return _jsx(AdminLogin, { onLogin: onLogin, onClose: () => navigate('/'), redirectToLogin: true });
    }
    return (_jsx(Suspense, { fallback: routeFallback, children: _jsx(AdminPanel, { onLogout: onLogout }) }));
};
const Layout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
    const { site } = useSiteData();
    const compactNav = useMediaQuery('(max-width: 959px)');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [headerScrolled, setHeaderScrolled] = useState(false);
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);
    useEffect(() => {
        const onScroll = () => setHeaderScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    useEffect(() => {
        if (!mobileNavOpen)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                setMobileNavOpen(false);
        };
        const onClick = (e) => {
            const t = e.target;
            if (!t?.closest?.('.navigation') && !t?.closest?.('.menu-toggle')) {
                setMobileNavOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('click', onClick);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('click', onClick);
        };
    }, [mobileNavOpen]);
    useEffect(() => {
        if (!compactNav && mobileNavOpen)
            setMobileNavOpen(false);
    }, [compactNav, mobileNavOpen]);
    useEffect(() => {
        if (location.hash) {
            const id = decodeURIComponent(location.hash.replace(/^#/, ''));
            const scrollToHash = () => {
                const el = document.getElementById(id);
                if (el)
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };
            const t = window.setTimeout(scrollToHash, 80);
            return () => window.clearTimeout(t);
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [location.pathname, location.hash]);
    const handleAdminLogin = (success) => {
        if (success) {
            setIsAdminLoggedIn(true);
            navigate('/admin/altalanos');
        }
    };
    const brandMark = publicAssetUrl('/pictures/mock-brand-mark.png');
    return (_jsxs(_Fragment, { children: [_jsx("a", { href: "#main-content", css: css `position:absolute;top:-100px;left:1rem;padding:0.75rem;background:#e51e2b;color:#fff;border-radius:8px;z-index:10001;font-weight:800`, children: "Ugr\u00E1s a tartalomra" }), _jsxs("header", { className: `header${headerScrolled ? ' header-scrolled' : ''}`, role: "banner", children: [_jsxs(NavLink, { to: "/", className: "brand", onClick: () => setMobileNavOpen(false), css: css `text-decoration:none;color:inherit`, children: [_jsx("span", { className: "brand-mark", children: _jsx("img", { src: site.logoUrl || brandMark, alt: "Minimal Car log\u00F3" }) }), _jsx("span", { children: _jsx("strong", { children: _jsx(SplitLetters, { text: "MINIMAL CAR", className: "brand-letter" }) }) })] }), _jsxs("button", { type: "button", className: "menu-toggle", id: "menuButton", "aria-expanded": mobileNavOpen, "aria-controls": "navigation", onClick: (e) => {
                            e.stopPropagation();
                            setMobileNavOpen((o) => !o);
                        }, children: ["\u2630 ", _jsx("span", { children: "MEN\u00DC" })] }), _jsx("nav", { className: `navigation${mobileNavOpen ? ' open' : ''}`, id: "navigation", "aria-label": "F\u0151 navig\u00E1ci\u00F3", children: _jsx(MainNavLinks, { onNavigate: () => setMobileNavOpen(false) }) })] }), _jsx("main", { id: "main-content", css: css `
          min-height: calc(100vh - 72px);
          background: #f5f5f5;
          width: 100%;
          overflow-x: clip;
        `, role: "main", children: _jsx(Suspense, { fallback: routeFallback, children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(HomePage, {}) }), _jsx(Route, { path: "/rolunk", element: _jsx(RolunkPage, {}) }), _jsx(Route, { path: "/kinalat", element: _jsx(Navigate, { to: "/", replace: true }) }), _jsx(Route, { path: "/kapcsolat", element: _jsx(KapcsolatPage, {}) }), _jsx(Route, { path: "/autok/:id", element: _jsx(Navigate, { to: "/", replace: true }) }), _jsxs(Route, { path: "/admin", element: _jsx(AdminEntryWrapper, { isLoggedIn: isAdminLoggedIn, onLogin: handleAdminLogin, onLogout: () => {
                                        setIsAdminLoggedIn(false);
                                        navigate('/');
                                    } }), children: [_jsx(Route, { index: true, element: _jsx(Navigate, { to: "altalanos", replace: true }) }), _jsx(Route, { path: "autok", element: _jsx(Navigate, { to: "/admin/altalanos", replace: true }) }), _jsx(Route, { path: "altalanos", element: _jsx(Suspense, { fallback: routeFallback, children: _jsx(AdminSettings, {}) }) })] })] }) }) }), _jsxs("footer", { css: css `
          background: #080b0d;
          color: #bbb;
          padding: clamp(1.75rem, 5vw, 2.75rem) clamp(0.65rem, 3.5vw, 1rem) clamp(1.35rem, 4vw, 2rem);
          border-top: 1px solid rgba(255, 202, 19, 0.35);
        `, role: "contentinfo", children: [_jsxs("div", { css: css `
            box-sizing: border-box;
            width: 100%;
            max-width: 1200px;
            margin: 0 auto;
            min-width: 0;
            display: grid;
            gap: clamp(1.25rem, 4vw, 1.85rem);
            grid-template-columns: 1fr;
            @media (min-width: 760px) {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
          `, children: [_jsxs("div", { children: [_jsx("h2", { css: css `
                margin: 0 0 0.55rem;
                font-family: 'Barlow Condensed', 'Teko', Impact, sans-serif;
                font-size: 1.55rem;
                color: #fff;
                font-weight: 800;
                letter-spacing: 0.04em;
                text-transform: uppercase;
              `, children: "Minimal Car" }), _jsx("p", { css: css `margin:0;line-height:1.65;color:#b3b3b3;font-size:0.93rem`, children: "Haszn\u00E1ltaut\u00F3 keresked\u00E9s Ny\u00EDregyh\u00E1z\u00E1n \u2013 k\u00E9szp\u00E9nzes felv\u00E1s\u00E1rl\u00E1s, korrekt \u00FCgyint\u00E9z\u00E9s." })] }), _jsxs("div", { children: [_jsx("h3", { css: css `margin:0 0 0.55rem;font-size:0.78rem;color:#e51e2b;text-transform:uppercase;letter-spacing:0.12em;font-weight:800`, children: "Kapcsolat" }), _jsx("p", { css: css `margin:0 0 0.35rem;overflow-wrap:break-word`, children: _jsx("a", { href: `tel:${site.phoneTel.replace(/\s/g, '')}`, css: css `
                  color: #fff;
                  font-weight: 800;
                  font-size: 1.05rem;
                  word-break: break-word;
                  &:hover {
                    color: #e51e2b;
                  }
                `, children: site.phoneDisplay }) }), site.contactEmail.trim() ? (_jsx("p", { css: css `margin:0.35rem 0 0;font-size:0.88rem;overflow-wrap:anywhere;color:#d1cfc9`, children: site.contactEmail.trim() })) : null, _jsx("p", { css: css `margin:0.5rem 0 0;font-size:0.85rem;color:#9a9590;line-height:1.5;overflow-wrap:break-word`, children: site.contactAddressDisplay })] }), _jsxs("div", { children: [_jsx("h3", { css: css `margin:0 0 0.55rem;font-size:0.78rem;color:#e51e2b;text-transform:uppercase;letter-spacing:0.12em;font-weight:800`, children: "Nyitvatart\u00E1s" }), _jsx("p", { css: css `margin:0;line-height:1.8;font-size:0.92rem;color:#c9c9c9;white-space:pre-line`, children: site.openingHoursLong })] })] }), _jsxs("div", { className: "social-links", css: css `padding-top:1rem`, children: [_jsx("a", { href: site.facebookUrl, target: "_blank", rel: "noopener noreferrer", children: "Facebook \u2197" }), _jsx("a", { href: "https://www.tiktok.com/@minimal_car", target: "_blank", rel: "noopener noreferrer", children: "TikTok \u2197" })] }), _jsxs("p", { css: css `
            box-sizing: border-box;
            width: 100%;
            max-width: 1200px;
            margin: clamp(0.75rem, 3vw, 1.25rem) auto 0;
            min-width: 0;
            padding: clamp(0.85rem, 3vw, 1.15rem) clamp(0.5rem, 2vw, 0) 0;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            color: #888;
            font-size: clamp(0.76rem, 2.2vw, 0.82rem);
            text-align: center;
            line-height: 1.5;
          `, children: ["\u00A9 ", _jsx("span", { id: "year", children: new Date().getFullYear() }), " Minimal Car Kft. Minden jog fenntartva."] })] })] }));
};
export default Layout;
